import { BrowserLogger } from "@ciaopeople/cp-shatsu/Logging";
import { isNumber } from "@ciaopeople/cp-shatsu/TypeChecking";
import { OuterResolvablePromise, PromiseOnSteroids } from "../../../../lib/utils/PromiseOnSteroids.js";
import { AdBreakType } from "../../../ads/AdUnit.js";
import { AdInfoStruct, PlayerAdStartedEvent } from "../../../ads/PlayerAdEvent.js";
import { ExternallyPassedContentAdData } from "../../../ads/adcall/composable/ContentSpecificAdCallFilter.js";
import { PlayerEvent, isMediaEvent } from "../../../events/PlayerEvent.js";
import { ExecutionId } from "../../../player/MediaResource.js";
import { ComScoreConfig, ComScoreStreamingTag, ComScoreStreamingTagFactory } from "./ComScoreStreamingTag.js";
import { NoOpComScoreStreamingTag } from "./ComScoreNoOp.js";
import "./comscore.types.js";
import "./comscore.wrapper.js";
import { MediaInfoRawData } from "../../../../model/MediaInfo.js";
import { AudicomConfig } from "../AudicomPairingPing.js";
import { getContestoBrowser } from "@ciaopeople/cp-shatsu/ClientDetection";
import { CustomWindow } from "@ciaopeople/cp-shatsu/Ads";
import { generateUID } from "../../../../lib/utils/Utility.js";
import { GDPRSdk } from "@ciaopeople/cp-shatsu/GDPR";

export class ComScoreStreamingTagFactoryImpl implements ComScoreStreamingTagFactory {


    private analyticsPromise: Promise<ns_.analytics.StreamingAnalytics | null>;

    private currentPromise: OuterResolvablePromise<ComScoreStreamingTag>;
    private currentExecutionId: ExecutionId;
    private currentMediaInfo?: MediaInfoRawData<any>;
    private audicomVideoSdkConsent: string | null = null;
    constructor(
        private config: ComScoreConfig,
        private gdprPromise: Promise<GDPRSdk>,
        private window: Window,
        private externalData?: ExternallyPassedContentAdData,
        private audicom?: AudicomConfig
    ) {

        this.analyticsPromise = this.initPromise();
    }




    private initPromise(): Promise<ns_.analytics.StreamingAnalytics | null> {

        return this.gdprPromise.then(gdpr => {
            
            // Consenso all'SDK Video Unico (Comscore) per la Ricerca Integrata Audicom.
            this.audicomVideoSdkConsent = gdpr.audicom.videoAudicomEnabled() ? "Y" : "N";

            // Opt-out Audicom: l'utente si oppone anche alla rilevazione volumetrica.
            // In questo caso l'SDK Video Comscore NON deve essere inizializzato, così
            // Auditel non riceve alcun tracciamento SDK (Doc E, Caso 5). Vale solo in
            // contesto Audicom: senza config audicom il percorso Comscore "proprio" resta invariato.
            const isVolumetricOptOut = !gdpr.audicom.volumetricEnabled();
            if (this.audicom && isVolumetricOptOut) {
                log.debug("[AUDICOM] Opt-out volumetrica: SDK Video Comscore non inizializzato");
                return null;
            }

            // cs_ucfr = consenso alla misurazione arricchita (cookie). In contesto Audicom
            // arriva da videoAudicomEnabled() (Y/N), NON da generalEnable(): quest'ultimo ora
            // coincide con volumetricEnabled() e serve solo all'opt-out (già gestito dal gate sopra).
            // Nel path Comscore "proprio" (senza audicom) resta generalEnable().
            const comscoreCookieConsent = this.audicom
                ? this.audicomVideoSdkConsent === "Y"
                : gdpr.comscore.generalEnable();

            const persistent: ns_.analytics.AllTrackingLabels = {
                cs_ucfr: comscoreCookieConsent ? ns_.analytics.UserConsent.CONSENT_GIVEN : ns_.analytics.UserConsent.NO_CONSENT,

            }

            ns_.analytics.PlatformApi.setPlatformAPI(ns_.analytics.PlatformAPIs.WebBrowser);
            const pubconf = new ns_.analytics.configuration.PublisherConfiguration({
                publisherId: this.config.publisherId,
                persistentLabels: persistent
            });

            ns_.analytics.configuration.addClient(pubconf);
            ns_.analytics.configuration.setApplicationName("Youmedia");
            ns_.analytics.configuration.setApplicationVersion("1.1");
            ns_.analytics.configuration.enableFirstPartyCookie();
            ns_.analytics.configuration.setUsagePropertiesAutoUpdateMode(
                ns_.analytics.configuration.UsagePropertiesAutoUpdateMode.FOREGROUND_AND_BACKGROUND
            );
            if (this.config.debug) {
                ns_.analytics.configuration.setDebugEnabled(true);
                ns_.analytics.configuration.enableImplementationValidationMode();
            }
            ns_.analytics.start();
            const sa = new ns_.analytics.StreamingAnalytics({
                publisherId: this.config.publisherId
            })

            sa.setProjectId(this.config.projectId || "YoumediaWebPlayerProject");
            sa.setMediaPlayerName("Youmedia Player");
            sa.setMediaPlayerVersion("1.0.0");
            sa.setImplementationId(this.config.implementationId || "YoumediaWebPlayer"); // TODO: da confermare, inserito a caso    
            return sa;
        });


    }


    getStreamingTag(mle: PlayerEvent): Promise<ComScoreStreamingTag> {

        log.debug("[getStreamingTag] event:", mle.executionId, "type:", (mle as any).eventType, "isMediaEvent:", isMediaEvent(mle), "currentExecId:", this.currentExecutionId);

        if (!mle.executionId || mle.executionId === "no-available-id") {
            log.debug("[getStreamingTag] Skipped - invalid executionId:", mle.executionId);
            return;
        }

        if (this.currentExecutionId !== mle.executionId) {
            if (!isMediaEvent(mle) && !this.currentMediaInfo) {
                log.debug("[getStreamingTag] Skipped - new executionId but no info available");
                return;
            }
            log.debug("[getStreamingTag] New executionId, creating streaming tag. old:", this.currentExecutionId, "new:", mle.executionId);
            this.currentExecutionId = mle.executionId;
            if (isMediaEvent(mle)) {
                this.currentMediaInfo = mle.info;
            }
            this.currentPromise = PromiseOnSteroids.getResolvable();
            this.currentPromise.chainResolve(this.analyticsPromise.then(sa => {
                if (!sa) {
                    log.debug("[getStreamingTag] Opt-out attivo: ritorno tag no-op per executionId:", mle.executionId);
                    return new NoOpComScoreStreamingTag(this.currentMediaInfo, mle.executionId);
                }
                log.debug("[getStreamingTag] StreamingTag created for executionId:", mle.executionId);
                return new ComScoreSteamingTagImpl(sa, this.currentMediaInfo, mle.executionId, this.window, this.externalData, this.audicom, this.audicomVideoSdkConsent);
            }));
        } else {
            log.debug("[getStreamingTag] Reusing existing tag for executionId:", mle.executionId);
        }

        return this.currentPromise.promise;
    }

}


const mappedCategories: { [k: string]: string } =
    ["gossip", "music", "cinema", "tv"].map(e => [e, "spettacolo"]).concat(
        ["tech", "games"].map(e => [e, "tecnologia"])
    ).concat(
        ["donna", "goquiz", "youmedia", "motori", "design",
            "scienze", "calcio", "autori", "roma", "napoli",
            "milano", "sondaggi", "cucina"].map(e => [e, e])
    ).concat(
        [["job", "lavoro"], ["www", "cronaca"], ["viaggi", "travel"]]
    ).reduce((acc, coup) => {
        const [k, v] = coup;
        acc[k] = v;
        return acc;
    }, {})


function categoryMapping(info: MediaInfoRawData<any>, maybeExt?: ExternallyPassedContentAdData): string {
    const ucat = (maybeExt?.area ? maybeExt.area : (info.meta?.category ? info.meta.category : "invalid_category")).toLowerCase();
    return (ucat in mappedCategories ? mappedCategories[ucat] : "youmedia");
}


type TimeSplit = [[year: number, month: number, day: number], [hour: number, min: number, sec: number]];


function timestampToTimeSplit(timestamp: number): TimeSplit {


    const d = new Date(isNumber(timestamp) && !isNaN(timestamp) && timestamp > 0 ? timestamp : 0);

    return [
        [
            d.getUTCFullYear(),
            d.getUTCMonth() + 1,
            d.getUTCDate()
        ],
        [
            d.getUTCHours(),
            d.getUTCMinutes(),
            d.getUTCSeconds()
        ]
    ];
}



function breakTypeToAdvertisementType(t: AdBreakType): ns_.analytics.StreamingAnalytics.AdvertisementMetadata.AdvertisementType {

    if (t == AdBreakType.PREROLL)
        return ns_.analytics.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.ON_DEMAND_PRE_ROLL;
    else if (t == AdBreakType.POSTROLL)
        return ns_.analytics.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.ON_DEMAND_POST_ROLL;
    else
        return ns_.analytics.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.ON_DEMAND_MID_ROLL;
}


const COMSCORE_NULL = "*null";



const SEEK_TOLERANCE = 0.5;

const log = new BrowserLogger("COMSCORE");


const CONSERVATIVE_METADATA_SET = true;

enum PlaybackState {
    IDLE = "idle",
    PLAYING = "playing",
    PAUSED = "paused"
}

class ComScoreSteamingTagImpl implements ComScoreStreamingTag {


    private cm: ns_.analytics.StreamingAnalytics.ContentMetadata;
    private currentAm: ns_.analytics.StreamingAnalytics.AdvertisementMetadata;

    private adState: PlaybackState;
    private neverCreated: boolean;
    private alreadyStartedVideo: boolean;



    constructor(
        private sa: ns_.analytics.StreamingAnalytics,
        public readonly currentMediaInfo: MediaInfoRawData<any>,
        public readonly currentExecutionId: ExecutionId,
        private window: Window,
        private externalData?: ExternallyPassedContentAdData,
        private audicom?: AudicomConfig,
        private sdk_consent?: string) {

        const use_title = currentMediaInfo.title.substring(0, 80);
        const [date, time] = timestampToTimeSplit(currentMediaInfo.meta?.publish_stamp);


        this.cm = new ns_.analytics.StreamingAnalytics.ContentMetadata();
        this.cm.setMediaType( currentMediaInfo.meta.duration >= 600 ? ns_.analytics.StreamingAnalytics.ContentMetadata.ContentType.LONG_FORM_ON_DEMAND : ns_.analytics.StreamingAnalytics.ContentMetadata.ContentType.SHORT_FORM_ON_DEMAND);
        this.cm.setUniqueId(currentMediaInfo.id);
        this.cm.setLength(Math.floor(currentMediaInfo.meta.duration * 1000));
        this.cm.setDictionaryClassificationC3(categoryMapping(currentMediaInfo, externalData));
        this.cm.setDictionaryClassificationC4(COMSCORE_NULL);
        this.cm.setDictionaryClassificationC6(COMSCORE_NULL);
        this.cm.setPublisherName("Ciaopeople");
        this.cm.setProgramTitle(use_title);
        this.cm.setGenreName(COMSCORE_NULL);
        this.cm.setStationTitle("Youmedia");
        this.cm.setEpisodeTitle(COMSCORE_NULL);
        this.cm.setGenreName(COMSCORE_NULL);
        this.cm.carryTvAdvertisementLoad(false);
        this.cm.classifyAsCompleteEpisode(true);
        this.cm.setDateOfProduction(date[0], date[1], date[2]);
        this.cm.setTimeOfProduction(time[0], time[1]);
        this.cm.setDateOfDigitalAiring(date[0], date[1], date[2]);
        this.cm.setTimeOfDigitalAiring(time[0], time[1]);
        this.cm.setDeliveryMode(ns_.analytics.StreamingAnalytics.ContentMetadata.ContentDeliveryMode.ON_DEMAND);
        this.cm.setDeliveryComposition(ns_.analytics.StreamingAnalytics.ContentMetadata.ContentDeliveryComposition.EMBED);
        this.cm.setDeliveryAdvertisementCapability(ns_.analytics.StreamingAnalytics.ContentMetadata.ContentDeliveryAdvertisementCapability.DYNAMIC_LOAD);
        this.cm.setDistributionModel(ns_.analytics.StreamingAnalytics.ContentMetadata.ContentDistributionModel.EXCLUSIVELY_ONLINE);
        this.cm.setClipUrl(currentMediaInfo.url ? currentMediaInfo.url : COMSCORE_NULL);

        this.setContentMetadataCustomLabels();

        this.neverCreated = true;
        this.alreadyStartedVideo = false;
        this.adState = PlaybackState.IDLE;
    }


    private setContentMetadataCustomLabels(): void {
        if (this.sdk_consent && (this.sdk_consent == "Y" || this.sdk_consent == "N") && this.audicom) {
            const customLabels = {
                oce_acom_schemaversion: 'S2SViewingDataV1.3',
                oce_acom_appid: this.audicom.appID, // app id nielsen
                oce_acom_producttype: 'DCR',
                oce_acom_apn: "Youmedia", // nome dell'app
                // streamid deterministico dall'executionId: deve coincidere con quello
                // del Pairing Ping (Doc F). Vedi generateUID() per il contratto completo.
                oce_acom_streamid: generateUID(this.currentExecutionId),
                oce_acom_sessionid: this.audicom.sessionID, // session id univoco,
                oce_acom_cons: this.sdk_consent, // flg di consenso Y, N, O,
                oce_bpf: `${this.externalData?.site || "unknown"}_${getContestoBrowser(this.window as CustomWindow)}`,
                cs_proid: "aco-it" 
            };

            console.log("[AUDICOM] Setting Content Metadata Custom Labels:", customLabels);
            this.cm.addCustomLabels(customLabels);
        }
    }

    private setAdvCustomLabels(adInfo: AdInfoStruct): void {
        if (this.sdk_consent && (this.sdk_consent == "Y" || this.sdk_consent == "N") && this.audicom) {

            const customLabels: { [k: string]: string } = {
                oce_acom_schemaversion: '*null',
                oce_acom_appid: '*null',
                oce_acom_producttype: '*null',
                oce_acom_apn: '*null',
                oce_acom_streamid: '*null',
                oce_acom_sessionid: '*null',
                oce_acom_cons: this.sdk_consent,
                oce_acom_pub_advid: this.audicom.appID,
                oce_skp: (adInfo.skipOffset || -1) + "",
                ns_st_ami: adInfo.cusv,
                cs_proid: "aco-it"
            };

            console.log("[AUDICOM] Setting Advertisting Custom Labels:", customLabels);
            this.currentAm.addCustomLabels(customLabels);
        }
    }


    private eventuallyCreateSession(): void {
        if (this.neverCreated) {
            log.debug("Creating Playback Session for executionId", this.currentExecutionId);
            log.debug("calling createPlaybackSession()");
            this.sa.createPlaybackSession();
            this.neverCreated = false;
        }
    }

    adStarted(evt: PlayerAdStartedEvent): void {
        // i non linear in background non devono essere conteggiati da comscore
        console.log(`${new Date().toLocaleTimeString()} - triggering ad event comscore`, evt)

        if (evt.adInfo.linear) {
            this.eventuallyCreateSession();
            if (this.currentAm) this.adEnded();
            this.alreadyStartedVideo = false;
            log.debug("AdStarted Event adbreak", evt.adBreak.label);
            this.currentAm = new ns_.analytics.StreamingAnalytics.AdvertisementMetadata();
            this.currentAm.setMediaType(breakTypeToAdvertisementType(evt.adBreak.breakType));
            this.currentAm.setRelatedContentMetadata(this.cm);
            this.setAdvCustomLabels(evt.adInfo);

            this.currentAm.setLength(Math.floor(evt.adInfo.duration * 1000));
            if (evt.adInfo.title) this.currentAm.setTitle(evt.adInfo.title);
            log.debug("calling setMetadata(advMetadata)");
            this.sa.setMetadata(this.currentAm);
            log.debug("calling notifyPlay()");
            this.sa.notifyPlay();
            this.adState = PlaybackState.PLAYING;
        }

    }
    adEnded(): void {
        if (this.currentAm && this.adState !== PlaybackState.IDLE) {
            this.eventuallyCreateSession();
            log.debug("AdEnded Event");
            if (!CONSERVATIVE_METADATA_SET) {
                log.debug("calling setMetadata(advMetadata)");
                this.sa.setMetadata(this.currentAm);
            }
            log.debug("calling notifyEnd()");
            this.sa.notifyEnd();
            this.currentAm = null;
            this.adState = PlaybackState.IDLE;
        }
    }

    adPaused(): void {
        if (this.currentAm && this.adState === PlaybackState.PLAYING) {
            this.eventuallyCreateSession();
            log.debug("AdPaused Event");
            if (!CONSERVATIVE_METADATA_SET) {
                log.debug("calling setMetadata(advMetadata)");
                this.sa.setMetadata(this.currentAm);
            }
            log.debug("calling notifyPause()");
            this.sa.notifyPause();
            this.adState = PlaybackState.PAUSED;
        }
    }

    adResumed(): void {
        if (this.currentAm && this.adState === PlaybackState.PAUSED) {
            this.eventuallyCreateSession();
            log.debug("AdResumed Event");
            if (!CONSERVATIVE_METADATA_SET) {
                log.debug("calling setMetadata(advMetadata)");
                this.sa.setMetadata(this.currentAm);
            }
            log.debug("calling notifyPlay()");
            this.sa.notifyPlay();
            this.adState = PlaybackState.PLAYING;
        }
    }

    play(currentTime: number): void {
        this.eventuallyCreateSession();
        this.currentAm = null;
        const shouldSeek = this.alreadyStartedVideo;
        if (!this.alreadyStartedVideo || !CONSERVATIVE_METADATA_SET) {
            log.debug("calling setMetadata(videoMetadata)");
            this.sa.setMetadata(this.cm);
            this.alreadyStartedVideo = true;
        }

        this.setContentMetadataCustomLabels();
        // if(currentTime  >= this.lastVideoTime + SEEK_TOLERANCE && 
        //     currentTime <= this.lastVideoTime - SEEK_TOLERANCE) {
        //     log.debug("seeking to:",currentTime);
        //     this.lastVideoTime = currentTime;
        if (shouldSeek) {
            log.debug("calling notifySeekStart()");
            this.sa.notifySeekStart();
            const curtime = Math.floor(currentTime * 1000);
            log.debug(`calling startFromPosition(${curtime})`);
            this.sa.startFromPosition(curtime);
        }
        // }
        log.debug("Invoking asset play with ctime", currentTime);
        log.debug("calling notifyPlay()");
        this.sa.notifyPlay();
    }
    pause(): void {
        this.eventuallyCreateSession();
        log.debug("Invoking asset pause");
        if (!CONSERVATIVE_METADATA_SET) {
            log.debug("calling setMetadata(videoMetadata)");
            this.sa.setMetadata(this.cm);
        }
        log.debug("calling notifyPause()");
        this.sa.notifyPause();
    }

    end(): void {
        this.eventuallyCreateSession();
        log.debug("Invoking asset end");
        if (!CONSERVATIVE_METADATA_SET) {
            log.debug("calling setMetadata(videoMetadata)");
            this.sa.setMetadata(this.cm);
        }
        log.debug("calling notifyEnd()");
        this.sa.notifyEnd();
    }

    bufferStart(): void {
        log.debug(`[bufferStart] ${new Date().toLocaleTimeString()} called for executionId:`, this.currentExecutionId);
        this.eventuallyCreateSession();
        log.debug("Invoking buffer start");
        if (!CONSERVATIVE_METADATA_SET) {
            log.debug("calling setMetadata(videoMetadata)");
            this.sa.setMetadata(this.cm);
        }
        this.sa.notifyBufferStart();
        log.debug(`[bufferStart] ${new Date().toLocaleTimeString()} - notifyBufferStart() sent`);
    }

    bufferEnd(): void {
        log.debug(`[bufferEnd] ${new Date().toLocaleTimeString()} - bufferEnd() called for executionId:`, this.currentExecutionId);
        this.eventuallyCreateSession();
        log.debug("Invoking buffer end");
        if (!CONSERVATIVE_METADATA_SET) {
            log.debug("calling setMetadata(videoMetadata)");
            this.sa.setMetadata(this.cm);
        }
        this.sa.notifyBufferStop();
        log.debug(`[bufferEnd] ${new Date().toLocaleTimeString()} - notifyBufferStop() sent`);
    }

}